import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SuperAdminService, {
  AiChatAdminConversation,
  AiChatAdminMessage,
  AiChatCompanyRow,
} from '../../services/SuperAdminService';

interface AiChatSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  company: { id: string; name: string } | null;
  /** Şirketin mevcut ayar/kullanım satırı (SuperAdmin listesinden) */
  row: AiChatCompanyRow | null;
  /** Toggle/limit değişince üst listeyi yenile */
  onChanged: () => void;
}

const formatNumber = (n: number) => n.toLocaleString('tr-TR');

/**
 * SuperAdmin — şirket bazlı AI chat ayar diyaloğu:
 * aç/kapat switch, aylık token limiti, bu ayki kullanım ve konuşma dökümü (denetim).
 */
const AiChatSettingsDialog: React.FC<AiChatSettingsDialogProps> = ({
  open,
  onClose,
  company,
  row,
  onChanged,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [planEnabled, setPlanEnabled] = useState(false);
  const [limitText, setLimitText] = useState('1000000');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [conversations, setConversations] = useState<AiChatAdminConversation[] | null>(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<AiChatAdminConversation | null>(null);
  const [messages, setMessages] = useState<AiChatAdminMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setEnabled(row?.enabled ?? false);
      setAgentEnabled(row?.agentEnabled ?? false);
      setPlanEnabled(row?.planEnabled ?? false);
      setLimitText(String(row?.monthlyTokenLimit ?? 1000000));
      setError(null);
      setSuccess(null);
      setConversations(null);
      setSelectedConversation(null);
    }
  }, [open, row]);

  if (!company) return null;

  const service = SuperAdminService.getInstance();
  const usedTokens = row?.usedTokens ?? 0;
  const limit = row?.monthlyTokenLimit ?? 0;
  const usagePct = limit > 0 ? Math.min(100, (usedTokens / limit) * 100) : 0;

  const handleToggle = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await service.toggleAiChat(company.id, next);
      setEnabled(next);
      setSuccess(next ? 'AI chat açıldı' : 'AI chat kapatıldı');
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAgent = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await service.toggleAiChatAgent(company.id, next);
      setAgentEnabled(next);
      setSuccess(next ? 'Ajan modu (AI Pro) açıldı' : 'Ajan modu (AI Pro) kapatıldı');
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePlan = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await service.toggleAiChatPlan(company.id, next);
      setPlanEnabled(next);
      setSuccess(next ? 'Plan modu açıldı' : 'Plan modu kapatıldı');
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  /** AI Pro kısayolu: ajan + plan modlarını birlikte açar/kapar. */
  const handleToggleAiPro = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await service.toggleAiChatAgent(company.id, next);
      setAgentEnabled(next);
      await service.toggleAiChatPlan(company.id, next);
      setPlanEnabled(next);
      setSuccess(next ? 'AI Pro paketi açıldı (ajan + plan)' : 'AI Pro paketi kapatıldı (ajan + plan)');
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLimit = async () => {
    const parsed = parseInt(limitText, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Geçerli bir limit girin');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await service.setAiChatLimit(company.id, parsed);
      setSuccess('Aylık limit güncellendi');
      onChanged();
    } catch (err: any) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadConversations = async () => {
    setConversationsLoading(true);
    setError(null);
    try {
      setConversations(await service.getAiChatConversations(company.id));
    } catch (err: any) {
      setError(err?.message || 'Konuşmalar yüklenemedi');
    } finally {
      setConversationsLoading(false);
    }
  };

  const handleOpenConversation = async (conversation: AiChatAdminConversation) => {
    setSelectedConversation(conversation);
    setMessagesLoading(true);
    try {
      setMessages(await service.getAiChatConversationMessages(conversation.id));
    } catch (err: any) {
      setError(err?.message || 'Mesajlar yüklenemedi');
      setSelectedConversation(null);
    } finally {
      setMessagesLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <AutoAwesomeIcon color="primary" />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            AI Chat — {company.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {selectedConversation ? (
          /* Konuşma dökümü görünümü */
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={() => setSelectedConversation(null)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2" noWrap sx={{ flexGrow: 1 }}>
                {selectedConversation.title || `Konuşma #${selectedConversation.id}`}
              </Typography>
              <Chip size="small" label={selectedConversation.userEmail || selectedConversation.userId} />
            </Box>
            {messagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {messages.map((m) => (
                  <Box
                    key={m.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: m.role === 'user' ? 'rgba(25, 118, 210, 0.06)' : 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600} color={m.role === 'user' ? 'primary' : 'text.secondary'}>
                        {m.role === 'user' ? 'Kullanıcı' : `Asistan${m.model ? ` (${m.model})` : ''}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(m.createdAt).toLocaleString('tr-TR')}
                        {m.totalTokens > 0 && ` • ${formatNumber(m.totalTokens)} token`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.content}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {/* Aç/kapat */}
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => handleToggle(e.target.checked)}
                  disabled={saving}
                />
              }
              label={enabled ? 'AI Chat Asistanı AÇIK' : 'AI Chat Asistanı KAPALI'}
            />

            {/* AI Pro: ajan modu — chat'te henüz kilitli (altyapı hazırlığı), paket ayrımı için */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={agentEnabled}
                    onChange={(e) => handleToggleAgent(e.target.checked)}
                    disabled={saving || !enabled}
                    color="secondary"
                  />
                }
                label={agentEnabled ? 'Ajan Modu (AI Pro) AÇIK' : 'Ajan Modu (AI Pro) KAPALI'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                Ajan modu kullanıcı arayüzünde henüz "Çok Yakında" olarak kilitlidir; bu anahtar paket ayrımı altyapısıdır.
              </Typography>
            </Box>

            {/* AI Pro: plan modu — çok adımlı görev planları (örn. şube açılışı) */}
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={planEnabled}
                    onChange={(e) => handleTogglePlan(e.target.checked)}
                    disabled={saving || !enabled}
                    color="secondary"
                  />
                }
                label={planEnabled ? 'Plan Modu (AI Pro) AÇIK' : 'Plan Modu (AI Pro) KAPALI'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                Plan modu çok adımlı görev planlarını (örn. şube açılışı) etkinleştirir; ajan moduyla birlikte kullanılır.
              </Typography>
            </Box>

            {/* AI Pro kısayolu: ajan + plan birlikte */}
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => handleToggleAiPro(!(agentEnabled && planEnabled))}
                disabled={saving || !enabled}
              >
                {agentEnabled && planEnabled ? 'AI Pro Paketini Kapat' : 'AI Pro Paketini Aç'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                AI Pro paketi ajan ve plan modlarını birlikte açar/kapatır.
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Aylık limit */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Aylık Token Limiti
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={limitText}
                onChange={(e) => setLimitText(e.target.value)}
                inputProps={{ min: 0, step: 10000 }}
                disabled={saving}
              />
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveLimit}
                disabled={saving}
                sx={{ flexShrink: 0 }}
              >
                Kaydet
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Bu ayki kullanım */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Bu Ayki Kullanım
            </Typography>
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={usagePct}
                color={usagePct >= 100 ? 'error' : usagePct >= 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 4, mb: 0.75 }}
              />
              <Typography variant="body2" color="text.secondary">
                {formatNumber(usedTokens)} / {formatNumber(limit)} token ({usagePct.toFixed(1)}%)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                İstek sayısı: {formatNumber(row?.requestCount ?? 0)} • Prompt: {formatNumber(row?.promptTokens ?? 0)} • Çıkış: {formatNumber(row?.outputTokens ?? 0)}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Konuşma dökümü */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Konuşmalar (Denetim)</Typography>
              <Button size="small" onClick={handleLoadConversations} disabled={conversationsLoading}>
                {conversationsLoading ? <CircularProgress size={16} /> : conversations ? 'Yenile' : 'Yükle'}
              </Button>
            </Box>
            {conversations && conversations.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Bu şirket için konuşma yok.
              </Typography>
            )}
            {conversations && conversations.length > 0 && (
              <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {conversations.map((c) => (
                  <ListItemButton key={c.id} onClick={() => handleOpenConversation(c)}>
                    <ListItemText
                      primary={c.title || `Konuşma #${c.id}`}
                      secondary={`${c.userEmail || c.userId} • ${c.messageCount} mesaj • ${formatNumber(c.totalTokens)} token • ${new Date(c.updatedAt).toLocaleString('tr-TR')}`}
                      primaryTypographyProps={{ noWrap: true, fontSize: 14 }}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AiChatSettingsDialog;
